<template>
  <b-modal
      :visible="visible"
      ref="modal"
      id="admin-subcategory-edit-modal"
      title="Add New Category"
      @hidden="$emit('cancel')"
      hide-footer
      centered
  >
    <b-form @submit.prevent="submitForm">

      <b-form-group label="Subcategory Name">
        <b-form-input
            v-model.trim="formData.name"
            type="text"
            :state="fieldValid('name')"
        />
        <b-form-invalid-feedback :state="fieldValid('name')">
          {{ formValidation.name }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Subcategory Belongs To">
        <b-form-select
            v-model="formData.belongs_to"
            :options="belongsToOptions"
            :state="fieldValid('belongs_to')"
        />
        <b-form-invalid-feedback :state="fieldValid('belongs_to')">
          {{ formValidation.belongs_to }}
        </b-form-invalid-feedback>
      </b-form-group>

      <template v-if="formData.belongs_to === 'category_type'">
        <b-form-group label="Associated Category Types">
          <div v-for="(ctId, key) in formData.category_types" :key="key" class="mb-1">
            <b-input-group>
              <b-form-input
                  :value="getCategoryTypeName(ctId)"
                  readonly
              />
              <template #append>
                <b-button variant="danger" @click="() => removeCategoryTypeId(key)">Remove</b-button>
              </template>
            </b-input-group>
          </div>
        </b-form-group>

        <b-form-group label="Add Category Type" v-if="categoryTypeOptions.length > 0">
          <b-input-group>
            <b-form-select
                v-model="categoryTypeIdToAdd"
                :options="categoryTypeOptions"
            />
            <template #append>
              <b-button variant="success" @click="addCategoryTypeId">Add</b-button>
            </template>
          </b-input-group>
        </b-form-group>
      </template>
      <template v-else-if="formData.belongs_to === 'category'">
        <b-form-group label="Associated Categories">
          <div v-for="(cId, key) in formData.categories" :key="key" class="mb-1">
            <b-input-group>
              <b-form-input
                  :value="getCategoryName(cId)"
                  readonly
              />
              <template #append>
                <b-button variant="danger" @click="() => removeCategoryId(key)">Remove</b-button>
              </template>
            </b-input-group>
          </div>
        </b-form-group>

        <b-form-group label="Add Category" v-if="categoryOptions.length > 0">
          <b-input-group>
            <b-form-select
                v-model="categoryIdToAdd"
                :options="categoryOptions"
            />
            <template #append>
              <b-button variant="success" @click="addCategoryId">Add</b-button>
            </template>
          </b-input-group>
        </b-form-group>
      </template>

      <b-button type="submit" variant="success" :disabled="formState.processing || !formData.belongs_to">
        Submit
        <b-spinner v-if="formState.processing" class="ml-1" small></b-spinner>
      </b-button>
      <b-btn type="button" variant="secondary" @click.prevent="close">Cancel</b-btn>

    </b-form>
  </b-modal>
</template>

<script>
import {API_ROUTES} from "../api";
import {sortingStrFunc} from "../helpers";

export default {
  name: "AdminSubcategoryEditModal",
  emits: ['success'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subcategory: {
      type: Object,
      required: true
    }
  },
  data() {
    let belongsTo = null;
    if (this.subcategory.belongs_to_category_type) {
      belongsTo = "category_type";
    } else if (this.subcategory.belongs_to_category) {
      belongsTo = "category";
    }

    return {
      formData: {
        name: this.subcategory.name,
        belongs_to: belongsTo,
        category_types: this.subcategory.category_types.map(ct => ct.id),
        categories: this.subcategory.categories.map(c => c.id)
      },
      formValidation: {},
      formState: {
        processing: false
      },
      belongsToOptions: [
        {
          value: "category_type",
          text: "Category Types"
        },
        {
          value: "category",
          text: "Categories"
        }
      ],
      categoryTypeIdToAdd: null,
      categoryIdToAdd: null,
    }
  },
  computed: {
    categoryTypes() {
      return [...this.$store.state.data.category_types].sort((first, second) => sortingStrFunc(first.name, second.name));
    },
    categoryTypeOptions() {
      return this.categoryTypes.filter(ct => this.formData.category_types.indexOf(ct.id) < 0).map(ct => ({
        value: ct.id,
        text: ct.name
      }));
    },
    categories() {
      let categories = [];

      this.categoryTypes.forEach(ct => {
        let ctCats = [...this.$store.state.data.categories].filter(c => c.category_type_id === ct.id)
            .sort((first, second) => sortingStrFunc(first.name, second.name))
            .map(c => ({
              ...c,
              name: `${ct.name} - ${c.name}`
            }));

        if (ctCats.length > 0) {
          categories = [
            ...categories,
            ...ctCats
          ]
        }
      });

      return categories;
    },
    categoryOptions() {
      return this.categories.filter(c => this.formData.categories.indexOf(c.id) < 0).map(c => ({
        value: c.id,
        text: c.name
      }));
    },
  },
  methods: {
    open() {
      this.$root.$emit('bv::show::modal', 'admin-subcategory-edit-modal');
    },
    close() {
      this.$root.$emit('bv::hide::modal', 'admin-subcategory-edit-modal');
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    addCategoryTypeId() {
      if (this.categoryTypeIdToAdd && this.formData.category_types.indexOf(this.categoryTypeIdToAdd) < 0) {
        this.formData.category_types = [...this.formData.category_types, this.categoryTypeIdToAdd];
      }
    },
    removeCategoryTypeId(index) {
      this.formData.category_types.splice(index, 1);
    },
    addCategoryId() {
      if (this.categoryIdToAdd && this.formData.categories.indexOf(this.categoryIdToAdd) < 0) {
        this.formData.categories = [...this.formData.categories, this.categoryIdToAdd];
      }
    },
    removeCategoryId(index) {
      this.formData.categories.splice(index, 1);
    },
    submitForm() {
      this.formState.processing = true;
      this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_SUBCATEGORIES}/${this.subcategory.id}`, this.formData).then(({data}) => {
        this.formValidation = {};
        this.$emit('success', data.subcategory);
        this.formData = {
          name: "",
          category_type: ""
        };
        this.close();
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update a subcategory', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    getCategoryTypeName(ctId) {
      let categoryType = this.$store.state.data.category_types.find(ct => ct.id === ctId);

      return categoryType ? categoryType.name : '';
    },
    getCategoryName(cId) {
      let category = this.$store.state.data.categories.find(c => c.id === cId),
          categoryName = '';
      if (category) {
        categoryName = category.name;
        let categoryType = this.$store.state.data.category_types.find(ct => ct.id === category.category_type_id);
        if (categoryType) {
          categoryName = `${categoryType.name} - ${categoryName}`;
        }
      }

      return categoryName;
    }
  }
}
</script>
